import { ChangeEvent, FormEvent, ReactNode, useState } from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { prefetchDetectedMarketHome } from 'actions/homeActions'
import typeStyle from 'styles/typeStyle'

import Button from '../../display/Button'
import ArrowRightSlimIcon from '../../icons/ArrowRightSlim'

import { TextField } from '..'

type AppearanceProps = {
  appearance?: 'flush' | 'raised'
  solidButton?: boolean
}

const TextFieldWrapper = styled.div<AppearanceProps>`
  position: relative;
  display: flex;
  width: 100%;
  flex: 1;

  ${TextField} {
    border-radius: 6px;
    border-color: white;
  }

  input[type='email'] {
    ${typeStyle('-ds-font-body-regular')}
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;

    ${({ appearance }) =>
      appearance === 'flush' &&
      `
      margin-right: 16px;
      `}
  }

  ${({ appearance, solidButton, theme }) =>
    appearance === 'raised' &&
    `
    margin: 16px 0;
    border-radius: 8px;
    background-color: ${theme?.['-ds-neutral-100']?.value};
    box-shadow: 0 1px 10px 0 rgb(144 116 102 / 20%);

    :focus-within {
      box-shadow: 0 1px 10px 0 rgb(144 116 102 / 40%);
    }

    ${
      solidButton &&
      `
      padding: 16px 16px 16px 0;
    `
    }
  `}
`

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
`

const SubmitButton = styled(Button)`
  && {
    flex-shrink: 0;
    margin: 0;
    padding: 14px 16px;
    background-color: ${({ theme }) => theme?.['-ds-core-300']?.value};
    color: ${({ theme }) => theme.colors.white};
  }

  svg {
    width: 28px;
    height: 28px;
    margin: 0;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 12px 0;
  width: 100%;

  label {
    margin: 0 0 12px 0;
    line-height: 26px;
    font-weight: inherit;
  }
`

const SuccessMessage = styled.div`
  font-size: 14px;
  line-height: 1.5;
`

type EmailSignupProps = AppearanceProps & {
  placeholder?: string
  labelInner?: ReactNode
  buttonLabel?: ReactNode
  marketCode?: string
}

const EmailSignup = ({
  appearance = 'flush',
  placeholder = 'Your email, of course!',
  labelInner,
  solidButton,
  buttonLabel = 'Subscribe',
  marketCode,
}: EmailSignupProps) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Pages without a URL-derived market (global home, country hubs, static
  // pages) fall back to the visitor's geo-detected market; on the home page
  // the prefetch promise has already settled, so this costs no extra request.
  const resolveMarket = (): Promise<string | undefined> => {
    if (marketCode) return Promise.resolve(marketCode)

    return prefetchDetectedMarketHome()
      .then(
        (response): string | undefined => response?.data?.currentMarket?.code,
      )
      .catch((): undefined => undefined)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!email) return
    if (typeof analytics === 'undefined') return

    const traits: {
      email: string
      market?: string
      locale?: string
      timezone?: string
    } = { email }

    // Show success (and remove the form) before awaiting market detection so
    // a slow lookup can't leave the form inert or accept duplicate submits.
    setSubmitted(true)
    setEmail('')

    const market = await resolveMarket()
    if (market) traits.market = market
    if (typeof navigator !== 'undefined' && navigator.language) {
      traits.locale = navigator.language
    }
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (tz) traits.timezone = tz
    } catch {
      // Intl unavailable — skip timezone
    }
    analytics.identify(undefined, traits, {
      integrations: { All: false, 'Customer.io (Actions)': true },
    })
  }

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        {labelInner && <label htmlFor="EMAIL">{labelInner}</label>}

        {submitted ? (
          <SuccessMessage role="status">
            Thanks — keep an eye on your inbox.
          </SuccessMessage>
        ) : (
          <TextFieldWrapper appearance={appearance} solidButton={solidButton}>
            <TextField
              placeholder={placeholder}
              type="email"
              name="EMAIL"
              id="EMAIL"
              autoComplete="email"
              value={email}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value)
              }
              required
            />
            <SubmitButton
              type="submit"
              name="subscribe"
              uppercase={false}
              aria-label="Subscribe to Riparide"
            >
              {largerThanMd ? buttonLabel : <ArrowRightSlimIcon />}
            </SubmitButton>
          </TextFieldWrapper>
        )}
      </Form>
    </FormContainer>
  )
}

export default EmailSignup
